/**
 * SA Tools - Process Table Component
 * Manage and score processes with AI assistance
 */
import React, { useState } from 'react';
import { Paper, Title, Table, Button, Group, Modal, TextInput, Textarea, Select, Stack, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconSparkles, IconEdit, IconTrash, IconDownload, IconWand } from '@tabler/icons-react';
import { ratingOptions, complexityOptions, scoreMapping } from '../../utils/constants';
import { convertToCSV, downloadCSV, getCSVColumns } from '../../utils/csvParser';
import aiService from '../../services/aiService';

function ProcessTable({ processes, setProcesses, contextData, onAIGenerate }) {
  const [modalOpened, setModalOpened] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    summary: '',
    impact_productivity: 'Medium-Low',
    importance_productivity: 'Medium-Low',
    impact_customer: 'Medium-Low',
    importance_customer: 'Medium-Low',
    impact_agent: 'Low',
    importance_agent: 'Low',
    impact_compliance: 'None',
    importance_compliance: 'None',
    complexity: 'Medium'
  });

  const calculateScore = (process) => {
    // Score formula: Σ(Impact × Importance) across all 4 categories
    // Maximum score: 4 × (1.0 × 1.0) = 4.0
    // Source: ipa-frontend scoreCalculator.js
    const prodImpact = scoreMapping[process.impact_productivity] || 0;
    const custImpact = scoreMapping[process.impact_customer] || 0;
    const agentImpact = scoreMapping[process.impact_agent] || 0;
    const complianceImpact = scoreMapping[process.impact_compliance] || 0;

    const prodImportance = scoreMapping[process.importance_productivity] || 0;
    const custImportance = scoreMapping[process.importance_customer] || 0;
    const agentImportance = scoreMapping[process.importance_agent] || 0;
    const complianceImportance = scoreMapping[process.importance_compliance] || 0;

    const total =
      (prodImpact * prodImportance) +
      (custImpact * custImportance) +
      (agentImpact * agentImportance) +
      (complianceImpact * complianceImportance);

    return total.toFixed(2);
  };

  const handleAddProcess = () => {
    setEditingProcess(null);
    setFormData({
      id: `proc-${Date.now()}`,
      name: '',
      summary: '',
      impact_productivity: 'Medium-Low',
      importance_productivity: 'Medium-Low',
      impact_customer: 'Medium-Low',
      importance_customer: 'Medium-Low',
      impact_agent: 'Low',
      importance_agent: 'Low',
      impact_compliance: 'None',
      importance_compliance: 'None',
      complexity: 'Medium'
    });
    setModalOpened(true);
  };

  const handleEditProcess = (process) => {
    setEditingProcess(process);
    setFormData({ ...process });
    setModalOpened(true);
  };

  const handleSaveProcess = () => {
    const processWithScore = {
      ...formData,
      total_score: calculateScore(formData)
    };

    if (editingProcess) {
      // Update existing
      setProcesses(prev => prev.map(p => p.id === processWithScore.id ? processWithScore : p));
    } else {
      // Add new
      setProcesses(prev => [...prev, processWithScore]);
    }

    setModalOpened(false);
  };

  const handleDeleteProcess = (id) => {
    if (window.confirm('Are you sure you want to delete this process?')) {
      setProcesses(prev => prev.filter(p => p.id !== id));
    }
  };

  const handleExportCSV = () => {
    const csvContent = convertToCSV(processes, getCSVColumns('final'));
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(csvContent, `processes-${timestamp}.csv`);
  };

  const handleRescoreProcess = async (process) => {
    if (!contextData || !contextData.customer) {
      alert('Please save customer context before using AI features');
      return;
    }

    try {
      // Call AI service to rescore this specific process
      const result = await aiService.rescoreProcess(process, contextData);

      // Update the process in the list (merge returned ratings into the existing process)
      setProcesses(prev => prev.map(p => p.id === process.id ? {
        ...p,
        ...result.ratings,
        total_score: calculateScore({ ...p, ...result.ratings })
      } : p));

      alert('Process rescored successfully!');
    } catch (error) {
      console.error('Error rescoring process:', error);
      alert(`Failed to rescore process: ${error.message}`);
    }
  };

  const handleRewriteSummary = async (process) => {
    if (!contextData || !contextData.customer) {
      alert('Please save customer context before using AI features');
      return;
    }

    try {
      const customerId = contextData.customer.toLowerCase().replace(/\s+/g, '-');

      // Call AI service to rewrite summary
      const updatedSummary = await aiService.rewriteProcessSummary(
        customerId,
        process.summary,
        contextData
      );

      // Update the process in the list (Lambda returns { success, summary })
      setProcesses(prev => prev.map(p => p.id === process.id ? {
        ...p,
        summary: updatedSummary.summary
      } : p));

      alert('Summary rewritten successfully!');
    } catch (error) {
      console.error('Error rewriting summary:', error);
      alert(`Failed to rewrite summary: ${error.message}`);
    }
  };

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={5}>
            <Title order={2} c="purple">Process Assessment</Title>
            <Text c="dimmed">Assess and rate processes across multiple impact dimensions and complexity levels. Use AI to generate ratings or manually configure each process.</Text>
          </Stack>
          <Group>
            {processes.length > 0 && (
              <Button
                leftSection={<IconDownload size={16} />}
                color="purple"
                variant="light"
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            )}
            {onAIGenerate && processes.length > 0 && (
              <Button
                leftSection={<IconSparkles size={16} />}
                color="purple"
                variant="light"
                onClick={() => onAIGenerate(processes, contextData)}
              >
                AI Generate Ratings
              </Button>
            )}
            <Button
              leftSection={<IconPlus size={16} />}
              color="purple"
              onClick={handleAddProcess}
            >
              Add Process
            </Button>
          </Group>
        </Group>

        {processes.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No processes added yet. Click "Add Process" to get started.
          </Text>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '100px' }}>Process ID</Table.Th>
                <Table.Th>Process Name</Table.Th>
                <Table.Th>Summary</Table.Th>
                <Table.Th style={{ width: '100px' }}>Complexity</Table.Th>
                <Table.Th style={{ width: '100px' }}>Total Score</Table.Th>
                <Table.Th style={{ width: '160px' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {processes.map((process, index) => (
                <Table.Tr key={process.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>PROC{index + 1}</Text>
                  </Table.Td>
                  <Table.Td>{process.name}</Table.Td>
                  <Table.Td style={{ maxWidth: '300px' }}>
                    <Text size="sm">{process.summary}</Text>
                  </Table.Td>
                  <Table.Td>{process.complexity}</Table.Td>
                  <Table.Td><strong>{process.total_score || calculateScore(process)}</strong></Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="Edit Process">
                        <ActionIcon color="blue" variant="light" onClick={() => handleEditProcess(process)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="AI Rescore Process">
                        <ActionIcon color="purple" variant="light" onClick={() => handleRescoreProcess(process)}>
                          <IconSparkles size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="AI Rewrite Summary">
                        <ActionIcon color="grape" variant="light" onClick={() => handleRewriteSummary(process)}>
                          <IconWand size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete Process">
                        <ActionIcon color="red" variant="light" onClick={() => handleDeleteProcess(process.id)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Modal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          title={editingProcess ? 'Edit Process' : 'Add Process'}
          size="xl"
        >
          <Stack gap="md">
            <TextInput
              label="Process Name"
              placeholder="Enter process name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />

            <Textarea
              label="Summary"
              placeholder="Enter process summary"
              minRows={6}
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              resize="vertical"
            />

            <Select
              label="Complexity"
              data={complexityOptions}
              value={formData.complexity}
              onChange={(value) => setFormData({ ...formData, complexity: value })}
            />

            <Title order={5}>Productivity/Efficiency</Title>
            <Group grow>
              <Select
                label="Impact"
                data={ratingOptions}
                value={formData.impact_productivity}
                onChange={(value) => setFormData({ ...formData, impact_productivity: value })}
              />
              <Select
                label="Importance"
                data={ratingOptions}
                value={formData.importance_productivity}
                onChange={(value) => setFormData({ ...formData, importance_productivity: value })}
              />
            </Group>

            <Title order={5}>Customer Experience</Title>
            <Group grow>
              <Select
                label="Impact"
                data={ratingOptions}
                value={formData.impact_customer}
                onChange={(value) => setFormData({ ...formData, impact_customer: value })}
              />
              <Select
                label="Importance"
                data={ratingOptions}
                value={formData.importance_customer}
                onChange={(value) => setFormData({ ...formData, importance_customer: value })}
              />
            </Group>

            <Title order={5}>Agent Experience</Title>
            <Group grow>
              <Select
                label="Impact"
                data={ratingOptions}
                value={formData.impact_agent}
                onChange={(value) => setFormData({ ...formData, impact_agent: value })}
              />
              <Select
                label="Importance"
                data={ratingOptions}
                value={formData.importance_agent}
                onChange={(value) => setFormData({ ...formData, importance_agent: value })}
              />
            </Group>

            <Title order={5}>Compliance</Title>
            <Group grow>
              <Select
                label="Impact"
                data={ratingOptions}
                value={formData.impact_compliance}
                onChange={(value) => setFormData({ ...formData, impact_compliance: value })}
              />
              <Select
                label="Importance"
                data={ratingOptions}
                value={formData.importance_compliance}
                onChange={(value) => setFormData({ ...formData, importance_compliance: value })}
              />
            </Group>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setModalOpened(false)}>
                Cancel
              </Button>
              <Button color="purple" onClick={handleSaveProcess} disabled={!formData.name}>
                {editingProcess ? 'Update' : 'Add'} Process
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Paper>
  );
}

export default ProcessTable;
